import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  computed,
  input,
  output
} from '@angular/core';
import { ContentTypes, ActionCardActions, ChatAnalyticAttributes, GoogleMaps } from '../../../classes/constants';
import { LocationService } from '../../../services/location.service';
import { ChatLoggingService } from '../../../services/chat-logging.service';
import { MessageEx } from '../../../classes/messageex';
import { ChatAuthService } from '../../../services/chat-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ScrollingService } from '../../../services/scrolling.service';
import { AnalyticObject } from '../../../interfaces/analytics-interfaces';
import type { QuoteMessageCite } from '../../chat/chat.component';
import type {
  OpenLinkEvent,
  PostWebhookPayloadEvent,
  ActionCardBehaviorEvent,
  ActionCardMessageEvent,
  GetCurrentLocationEvent
} from '../../../classes/actioncardhelpers';
import { default as AnalyticEvents } from '@mitel-internal/cloudlink-analytic-events/analytic-events.json';

@Component({
  selector: 'app-action-card',
  templateUrl: 'action-card.component.html',
  styleUrls: ['action-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionCardComponent {
  bodyObject = input<any>(undefined, { alias: 'bodyObject' });
  contentType = input<string>(undefined, { alias: 'contentType' });
  currentTabIndex = input<string>(undefined, { alias: 'currentTabIndex' });
  imageViewerUrl = input<any>(undefined, { alias: 'imageViewerUrl' });
  item = input<MessageEx>(undefined, { alias: 'item' });
  isOffline = input<boolean>(undefined, { alias: 'isOffline' });
  latitude = input<number>(undefined, { alias: 'latitude' });
  longitude = input<number>(undefined, { alias: 'longitude' });
  actionCardMaxWidth = input<string>(undefined, { alias: 'actionCardMaxWidth' });
  goingToQuoted = input<boolean>(undefined, { alias: 'goingToQuoted' });
  actionCardImageNearBottom = input<boolean>(undefined, { alias: 'actionCardImageNearBottom' });
  actionCardScrolledToMessage = input<boolean>(undefined, { alias: 'actionCardScrolledToMessage' });
  previewMode = input<boolean>(false);

  onLaunchDialer = output<any>();
  actionCardMaxWidthChange = output<string>();
  onActionCardBehavior = output<ActionCardBehaviorEvent>();
  onOpenLink = output<OpenLinkEvent>();
  @Output() onPostWebhookPayload: EventEmitter<PostWebhookPayloadEvent> = new EventEmitter<PostWebhookPayloadEvent>();
  @Output() onActionCardEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() onSendActionCardMessage: EventEmitter<ActionCardMessageEvent> = new EventEmitter<ActionCardMessageEvent>();
  @Output() onQuotedMessageChange: EventEmitter<QuoteMessageCite> = new EventEmitter<QuoteMessageCite>();
  @Output() onGetCurrentLocation: EventEmitter<GetCurrentLocationEvent> = new EventEmitter<GetCurrentLocationEvent>();
  public contentTypes = ContentTypes;
  public actionCardActions = ActionCardActions;
  private componentName = 'action-card';

  // Computed signal for Google Map ID
  googleMapId = computed(() => GoogleMaps.MAP_ID);

  constructor(
    private chatLoggingService: ChatLoggingService,
    public locationService: LocationService,
    public chatAuthService: ChatAuthService,
    public translateService: TranslateService,
    private scrollingService: ScrollingService
  ) {}

  actionCardBehavior(behavior: any, success: boolean) {
    let actionCardBehaviorEvent: ActionCardBehaviorEvent = {
      behavior: behavior,
      success: success
    };
    if (!this.previewMode()) {
      this.onActionCardBehavior.emit(actionCardBehaviorEvent);
    }
  }

  launchDialer($event: Event, tel: string, title: string, behavior: any) {
    if (!this.previewMode()) {
      this.onLaunchDialer.emit({
        $event: $event,
        tel: tel,
        title: title,
        behavior: behavior
      });
    }
  }

  openLink($event: Event, url: string, title: string, behavior: any) {
    if (!this.previewMode()) {
      let openLinkEvent: OpenLinkEvent = {
        $event: $event,
        url: url,
        title: title,
        behavior: behavior,
        componentName: this.componentName
      };
      this.onOpenLink.emit(openLinkEvent);
    }
  }

  postWebhookPayload($event: Event, url: string, payload: any, title: string, behavior: any, retry401: boolean = true) {
    if (!this.previewMode()) {
      let postWebhookPayloadEvent: PostWebhookPayloadEvent = {
        $event: $event,
        url: url,
        payload: payload,
        title: title,
        behavior: behavior,
        retry401: retry401,
        componentName: this.componentName
      };
      this.onPostWebhookPayload.emit(postWebhookPayloadEvent);
    }
  }

  public actionCardImageLoad(image) {
    if (!this.previewMode()) {
      if (image && image.naturalWidth) {
        if (image.naturalWidth < 360) {
          let newMaxWidth: string;
          if (image.naturalWidth > 180) {
            newMaxWidth = image.naturalWidth / 14 + 'rem';
          } else {
            newMaxWidth = '11.25rem';
          }
          this.actionCardMaxWidthChange.emit(newMaxWidth);
        }
      }
      setTimeout(() => {
        if (this.actionCardImageNearBottom() && !this.goingToQuoted() && !this.actionCardScrolledToMessage()) {
          this.scrollingService.scrollToBottom();
        } else if (this.actionCardScrolledToMessage() && !this.goingToQuoted()) {
          this.scrollingService.scrollToMessage(this.scrollingService.lastScrolledTo.messageId);
        }
      }, 0);
    }
  }

  public async sendActionMessage($event: Event, message: string, behavior: any) {
    if (!this.previewMode()) {
      try {
        if ($event) {
          $event.preventDefault;
        }
        let analyticObj: AnalyticObject = {
          eventName: AnalyticEvents.analytic_events.common.event_name.chat.send_action_card_message,
          attributes: ChatAnalyticAttributes
        };
        this.chatLoggingService.analyticEvent(analyticObj);
        let sendMessageEvent: ActionCardMessageEvent = {
          message: message || '',
          throwError: true
        };
        await this.onSendActionCardMessage.emit(sendMessageEvent);
        this.actionCardBehavior(behavior, true);
        this.chatLoggingService.info({
          logMessage: 'action-card.component.sendActionMessage() successs'
        });
      } catch (ex) {
        this.actionCardBehavior(behavior, false);
        this.chatLoggingService.error({
          logMessage: 'action-card.component.sendActionMessage() failure',
          logParameters: [ex]
        });
      }
    }
  }

  public async sendActionQuotedMessage(
    $event: Event,
    message: string,
    title: string,
    behavior: any,
    fallbackText: string = ''
  ) {
    if (!this.previewMode()) {
      try {
        if ($event) {
          $event.preventDefault;
        }
        var quotedMessage: QuoteMessageCite = {
          messageId: this.item().messageId,
          createdBy: this.item().createdBy,
          body: this.bodyObject().body
        };

        let analyticObj: AnalyticObject = {
          eventName: AnalyticEvents.analytic_events.common.event_name.chat.reply_action_card,
          attributes: ChatAnalyticAttributes
        };
        this.chatLoggingService.analyticEvent(analyticObj);
        this.onQuotedMessageChange.emit(quotedMessage);
        let sendMessageEvent: ActionCardMessageEvent = {
          message: message || '',
          fallbackText: fallbackText,
          throwError: true
        };
        await this.onSendActionCardMessage.emit(sendMessageEvent);
        this.actionCardBehavior(behavior, true);
        this.chatLoggingService.info({
          logMessage: 'action-card.component.sendActionQuotedMessage() success'
        });
      } catch (ex) {
        this.actionCardBehavior(behavior, false);
        this.chatLoggingService.error({
          logMessage: 'action-card.component.sendActionQuotedMessage() failure',
          logParameters: [ex]
        });
      }
    }
  }

  public async sendLocation($event, title: string, behavior: any) {
    if (!this.previewMode()) {
      try {
        if ($event) {
          $event.preventDefault;
        }
        let getCurrentLocationEvent: GetCurrentLocationEvent = {
          throwError: true,
          sendLocation: true
        };
        await this.onGetCurrentLocation.emit(getCurrentLocationEvent);
        this.actionCardBehavior(behavior, true);
        this.chatLoggingService.info({
          logMessage: 'action-card.component.sendLocation() success'
        });
      } catch (ex) {
        this.actionCardBehavior(behavior, false);
        this.chatLoggingService.error({
          logMessage: 'action-card.component.sendLocation() failure',
          logParameters: [ex]
        });
      }
    }
  }

  public sendUnsupported($event: Event, title: string) {
    if (!this.previewMode()) {
      $event.preventDefault();
      this.chatLoggingService.chatLocalError(
        this.translateService.instant('cl_chat.actionIsNotSupported', {
          action: title
        })
      );
    }
  }

  public raiseEvent($event: Event, payload: string, title: string, behavior: any) {
    if (!this.previewMode()) {
      if ($event) {
        $event.preventDefault;
      }
      let analyticObj: AnalyticObject = {
        eventName: AnalyticEvents.analytic_events.common.event_name.chat.raise_action_card_event,
        attributes: ChatAnalyticAttributes
      };
      this.chatLoggingService.analyticEvent(analyticObj);
      this.onActionCardEvent.emit(payload);
      this.actionCardBehavior(behavior, true);
      this.chatLoggingService.info({
        logMessage: 'action-card.component.raiseEvent() success'
      });
    }
  }
}
